// MoneyShortcuts.js
// Indian number formatting + Lakhs/Crores keyboard shortcuts for money inputs.

/**
 * Format a number into Indian comma style.
 * Example: 12345678 -> "1,23,45,678"
 */
export function formatIndianNumber(n, { maxFractionDigits = 0 } = {}) {
  if (n === null || n === undefined || n === '') return '';
  const num = typeof n === 'number' ? n : Number(String(n).replace(/,/g, '').trim());
  if (!Number.isFinite(num)) return '';
  return num.toLocaleString('en-IN', {
    maximumFractionDigits: maxFractionDigits,
    minimumFractionDigits: 0
  });
}

/**
 * Parse a number string that may contain Indian commas.
 * Example: "1,25,00,000" -> 12500000
 */
export function parseIndianNumber(text) {
  if (text === null || text === undefined) return NaN;
  const cleaned = String(text).replace(/,/g, '').trim();
  if (!cleaned) return NaN;
  const num = Number(cleaned);
  return Number.isFinite(num) ? num : NaN;
}

/**
 * Convert typed base number into rupees based on keyboard shortcuts.
 *  K / T => Thousand (x 1e3)
 *  L     => Lakhs   (x 1e5)
 *  C     => Crores  (x 1e7)
 * Returns integer rupees (rounded).
 */
export function applyLakhCroreShortcut(rawText, key) {
  const base = parseIndianNumber(rawText);
  if (!Number.isFinite(base)) return null;

  const k = String(key).toLowerCase();
  let rupees;

  if (k === 'k' || k === 't') rupees = base * 1000; // 1 Thousand
  else if (k === 'l') rupees = base * 100000; // 1 Lakh
  else if (k === 'c') rupees = base * 10000000; // 1 Crore
  else return null;

  return Math.round(rupees);
}

/**
 * React-friendly handler: attach to onKeyDown of amount inputs.
 *
 * Example:
 *   onKeyDown={(e) => handleMoneyShortcutKeyDown(e, form.monthlyIncome, (v) => setForm(f => ({...f, monthlyIncome: v}))) }
 */
export function handleMoneyShortcutKeyDown(e, currentValue, setValue) {
  const key = e?.key ? String(e.key).toLowerCase() : '';
  if (key !== 'k' && key !== 't' && key !== 'l' && key !== 'c') return;

  // Don't trigger for modified key combos
  if (e.ctrlKey || e.altKey || e.metaKey) return;

  const raw = currentValue ?? e.currentTarget?.value ?? '';
  const rupees = applyLakhCroreShortcut(raw, key);
  if (rupees === null) return;

  // Prevent the letter from being typed into the box
  e.preventDefault();

  // Store as rupees with Indian commas (best for calculations)
  setValue(formatIndianNumber(rupees));
}

/**
 * Optional: format with 2 decimals (useful for EMI display).
 * Example: 12345.678 -> "12,345.68" (Indian style)
 */
export function formatIndian2dp(n) {
  if (n === null || n === undefined || n === '') return '';
  const num = typeof n === 'number' ? n : Number(String(n).replace(/,/g, '').trim());
  if (!Number.isFinite(num)) return '';
  return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Format a value as INR with rupee symbol (₹) using Indian grouping.
 * decimals=2 will show paise; decimals=0 shows whole rupees.
 */
export function formatINR(value, { decimals = 2 } = {}) {
  if (value === null || value === undefined || value === '') return '—';
  const num = typeof value === 'number'
    ? value
    : Number(String(value).replace(/,/g, '').replace(/[₹\s]/g, '').trim());
  if (!Number.isFinite(num)) return '—';
  return '₹' + num.toLocaleString('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
}

export function formatINR2dp(value) {
  return formatINR(value, { decimals: 2 });
}

export function formatINR0dp(value) {
  return formatINR(value, { decimals: 0 });
}

/**
 * Parse an INR-ish string into a number.
 * Accepts commas and optional ₹ symbol.
 */
export function parseINR(text) {
  if (text === null || text === undefined) return NaN;
  const cleaned = String(text).replace(/,/g, '').replace(/[₹\s]/g, '').trim();
  if (!cleaned) return NaN;
  const num = Number(cleaned);
  return Number.isFinite(num) ? num : NaN;
}
